/**
 * Toast Viewport
 *
 * Renders queued toasts in a fixed stack. Mounted once, next to <App />, so it
 * survives every route/role switch the portal makes. It renders above the
 * app, beside the dialogs (abovePage): the app is inert while a dialog is open
 * (modal-layers.ts), and a toast can be closed over one.
 *
 * A toast is said, not just shown, through two live regions that stay mounted
 * and empty of anything of their own, polite and assertive: each toast's words
 * are added to one as it arrives, which a screen reader announces. Each toast
 * was its own region before, inserted with its words already in it, which a
 * screen reader may not announce, and the viewport was not on the page at all
 * until the first toast came. The cards themselves are not regions, so a toast
 * is not said twice.
 */

import { useTranslation } from 'react-i18next';
import { abovePage } from '../modal-layers';
import { useToastStore } from './toast-store';
import type { ToastType } from './toast-store';
import toastStyles from './toast.module.css';
import utilities from '../../styles/utilities.module.css';

/**
 * The three toast tones. Keyed rather than written as `toast--${type}`, which
 * a module resolves to nothing — an error toast would render in the neutral
 * style and still say "error" out loud to a screen reader.
 */
const TOAST_TONE: Record<ToastType, string> = {
  success: toastStyles.toastSuccess,
  error: toastStyles.toastError,
  info: toastStyles.toastInfo,
};

export function ToastViewport() {
  const { t } = useTranslation();
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);

  /** A toast's words, as its region says them. */
  const said = (toast: (typeof toasts)[number]) => (
    <div key={toast.id}>
      <p>{toast.title}</p>
      {toast.message && <p>{toast.message}</p>}
    </div>
  );

  return abovePage(
    <>
      <div className={toastStyles.toastViewport}>
        {toasts.map((toast) => (
          <div key={toast.id} className={`${toastStyles.toast} ${TOAST_TONE[toast.type]}`}>
            <div className={toastStyles.toast__body}>
              <p className={toastStyles.toast__title}>{toast.title}</p>
              {toast.message && <p className={toastStyles.toast__message}>{toast.message}</p>}
            </div>
            <button
              type="button"
              className={toastStyles.toast__close}
              onClick={() => dismiss(toast.id)}
              aria-label={t('common.close')}
            >
              ×
            </button>
          </div>
        ))}
      </div>
      {/* On the page, read, and shown to no one. Errors interrupt a screen reader; success and info wait for a pause. */}
      {/* design-checks:allow-hidden-region — the regions the toasts' lines are rendered into, not a line. */}
      <div className={utilities.visuallyHidden} aria-live="polite">
        {toasts.filter((toast) => toast.type !== 'error').map(said)}
      </div>
      <div className={utilities.visuallyHidden} aria-live="assertive">
        {toasts.filter((toast) => toast.type === 'error').map(said)}
      </div>
    </>,
  );
}
